import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import CustomDomainSite from './components/CustomDomainSite';
import { isCustomProfileHost } from './utils/customDomain';

// Redirect that carries the query string through (Navigate alone drops it),
// so campaign links like /upgrade?src=upsell-t1 keep their tracking tokens.
const RedirectWithQuery = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

// Layout - Not lazy loaded as it's used by wrapper routes
import Layout from "./components/layout/Layout";

// Simple loading fallback that matches the site background
const PageLoader = () => (
  <div style={{ 
    minHeight: '100vh', 
    backgroundColor: '#f5f1eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid #e5e5e5',
      borderTop: '3px solid #c9a94d',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Lazy load all page components for route-based code splitting
// Public Pages
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Talents = lazy(() => import("./pages/Talents"));
const TalentProfile = lazy(() => import("./pages/TalentProfile"));
const NewFaces = lazy(() => import("./pages/NewFaces"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const PublicTalentProfile = lazy(() => import("./pages/PublicTalentProfile"));
const PublicRoster = lazy(() => import("./pages/PublicRoster"));
const PublicPitch = lazy(() => import("./pages/PublicPitch"));
const Pricing = lazy(() => import("./pages/Pricing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminImport = lazy(() => import("./pages/AdminImport"));
const AdminImportHistory = lazy(() => import("./pages/AdminImportHistory"));
const AdminClaimInvites = lazy(() => import("./pages/AdminClaimInvites"));
const AdminActivity = lazy(() => import("./pages/AdminActivity"));
const AdminCMSPages = lazy(() => import("./pages/AdminCMSPages"));
const AdminCMSNewPage = lazy(() => import("./pages/AdminCMSNewPage"));
const AdminCMSEditPage = lazy(() => import("./pages/AdminCMSEditPage"));
const AdminNewsPosts = lazy(() => import("./pages/AdminNewsPosts"));
const AdminNewsEditPost = lazy(() => import("./pages/AdminNewsEditPost"));
const AdminPitches = lazy(() => import("./pages/AdminPitches"));
const AdminCreatePitch = lazy(() => import("./pages/AdminCreatePitch"));
const AdminTalentSubmissions = lazy(() => import("./pages/AdminTalentSubmissions"));
const AdminSubmissionHub = lazy(() => import("./pages/AdminSubmissionHub"));
const TalentMySubmissions = lazy(() => import("./pages/TalentMySubmissions"));
const AdminTalentMonitor = lazy(() => import("./pages/AdminTalentMonitor"));
const CMSPage = lazy(() => import("./pages/CMSPage"));

// Pitch & Client Pages
const PitchView = lazy(() => import("./pages/PitchView"));
const SavedTalents = lazy(() => import("./pages/SavedTalents"));

// Jobs/Casting Calls
const AdminJobs = lazy(() => import("./pages/AdminJobs"));
const AdminJobDetail = lazy(() => import("./pages/AdminJobDetail"));
const AdminCreateJob = lazy(() => import("./pages/AdminCreateJob"));
const TalentJobs = lazy(() => import("./pages/TalentJobs"));

// New Casting System
const AdminCastings = lazy(() => import("./pages/AdminCastings"));
const AdminCastingEdit = lazy(() => import("./pages/AdminCastingEdit"));
const AdminCastingNew = lazy(() => import("./pages/AdminCastingNew"));
const AdminCastingDetail = lazy(() => import("./pages/AdminCastingDetail"));
const AdminPendingApprovals = lazy(() => import("./pages/AdminPendingApprovals"));
const TapeRequestPublic = lazy(() => import("./pages/TapeRequestPublic"));
const TapeShareView = lazy(() => import("./pages/TapeShareView"));
const CastingReview = lazy(() => import("./pages/CastingReview"));
const ProjectReview = lazy(() => import("./pages/ProjectReview"));
const GetProfile = lazy(() => import("./pages/GetProfile"));
const TalentTapeRequest = lazy(() => import("./pages/TalentTapeRequest"));
const TalentInbox = lazy(() => import("./pages/TalentInbox"));
const TalentAuditionSubmit = lazy(() => import("./pages/TalentAuditionSubmit"));
const PublicCastings = lazy(() => import("./pages/PublicCastings"));
const TalentSubmission = lazy(() => import("./pages/TalentSubmission"));
const PublicAuditionProject = lazy(() => import("./pages/PublicAuditionProject"));
const PublicAuditionRole = lazy(() => import("./pages/PublicAuditionRole"));
const PublicExtrasCalls = lazy(() => import("./pages/PublicExtrasCalls"));
const PublicExtrasCall = lazy(() => import("./pages/PublicExtrasCall"));
const PublicProductionShare = lazy(() => import("./pages/PublicProductionShare"));

// Client Management
const AdminClients = lazy(() => import("./pages/AdminClients"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminCompose = lazy(() => import("./pages/AdminCompose"));
const AdminProfileAccessCampaign = lazy(() => import("./pages/AdminProfileAccessCampaign"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const AdminTalentPackager = lazy(() => import("./pages/AdminTalentPackager"));
const AdminFeaturedTalent = lazy(() => import("./pages/AdminFeaturedTalent"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const AdminRoster = lazy(() => import("./pages/AdminRoster"));
const AdminBackgroundRoster = lazy(() => import("./pages/AdminBackgroundRoster"));
const AdminBackgroundInquiries = lazy(() => import("./pages/AdminBackgroundInquiries"));
const AdminBackgroundSignups = lazy(() => import("./pages/AdminBackgroundSignups"));
const AdminBackgroundPool = lazy(() => import("./pages/AdminBackgroundPool"));
const AdminWardrobeBreakdown = lazy(() => import("./pages/AdminWardrobeBreakdown"));
const AdminHeadshotImport = lazy(() => import("./pages/AdminHeadshotImport"));
const BackgroundAvailability = lazy(() => import("./pages/BackgroundAvailability"));
const AdminTalentProfileEdit = lazy(() => import("./pages/AdminTalentProfileEdit"));
const AdminSiteEditor = lazy(() => import("./pages/AdminSiteEditor"));
const AdminSiteSettings = lazy(() => import("./pages/AdminSiteSettings"));
const AdminCastingDirectors = lazy(() => import("./pages/AdminCastingDirectors"));

// Talent Portal
const TalentDashboard = lazy(() => import("./pages/TalentDashboard"));
const TalentEditProfile = lazy(() => import("./pages/TalentEditProfile"));
const TalentAnalytics = lazy(() => import("./pages/TalentAnalytics"));
const TalentResume = lazy(() => import("./pages/TalentResume"));
const TalentSelfTapes = lazy(() => import("./pages/TalentSelfTapes"));
const TalentVoiceOver = lazy(() => import("./pages/TalentVoiceOver"));
const TalentSettings = lazy(() => import("./pages/TalentSettings"));
const TalentCastingDirectors = lazy(() => import("./pages/TalentCastingDirectors"));
const TalentExtrasCalls = lazy(() => import("./pages/TalentExtrasCalls"));
const AdminExtrasCalls = lazy(() => import("./pages/AdminExtrasCalls"));
const AdminExtrasCallSubmissions = lazy(() => import("./pages/AdminExtrasCallSubmissions"));
const AdminProductionUsers = lazy(() => import("./pages/AdminProductionUsers"));
const ProductionPortal = lazy(() => import("./pages/ProductionPortal"));
const ProductionSubmissions = lazy(() => import("./pages/ProductionSubmissions"));
const ProductionBlasts = lazy(() => import("./pages/ProductionBlasts"));
const AdminExtrasBlasts = lazy(() => import("./pages/AdminExtrasBlasts"));
const AdminBlastApprovals = lazy(() => import("./pages/AdminBlastApprovals"));
const ExtrasConfirm = lazy(() => import("./pages/ExtrasConfirm"));
const BgConfirm = lazy(() => import("./pages/BgConfirm"));
const TalentCalendar = lazy(() => import("./pages/TalentCalendar"));
const TalentReelSplit = lazy(() => import("./pages/TalentReelSplit"));
const TalentProfileEditor = lazy(() => import("./pages/TalentProfileEditor"));
const TalentAccount = lazy(() => import("./pages/TalentAccount"));
const TalentHelp = lazy(() => import("./pages/TalentHelp"));
const TalentHelpArticle = lazy(() => import("./pages/TalentHelpArticle"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ProfileAccess = lazy(() => import("./pages/ProfileAccess"));
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));

function App() {
  if (isCustomProfileHost()) {
    return <HelmetProvider><BrowserRouter><CustomDomainSite /></BrowserRouter></HelmetProvider>;
  }
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Auth pages without layout */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Login />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/profile-access" element={<ProfileAccess />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              <Route path="/pricing" element={<Pricing />} />
              {/* Marketing aliases — emails and campaigns link these; keep them working forever */}
              <Route path="/upgrade" element={<RedirectWithQuery to="/pricing" />} />
              <Route path="/pro" element={<RedirectWithQuery to="/pricing" />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              
              {/* Public Talent Profile - SEO-friendly URL /talent/firstname-lastname */}
              <Route path="/talent/:slug" element={<PublicTalentProfile />} />
              
              {/* Legacy URL - redirect to new format */}
              <Route path="/t/:slug" element={<PublicTalentProfile />} />
              
              {/* Public Roster Page */}
              <Route path="/roster" element={<PublicRoster />} />
              
              {/* Public Pitch View - for sharing with casting directors */}
              <Route path="/pitch/:pitchId" element={<PublicPitch />} />
              
              {/* Legal Pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              
              {/* Admin pages without main layout */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
              <Route path="/admin/import" element={<AdminImport />} />
              <Route path="/admin/import/history" element={<AdminImportHistory />} />
              <Route path="/admin/claim-invites" element={<AdminClaimInvites />} />
              <Route path="/admin/activity" element={<AdminActivity />} />
              <Route path="/admin/talent-submissions" element={<AdminTalentSubmissions />} />
              <Route path="/admin/talent-monitor" element={<AdminTalentMonitor />} />
              <Route path="/admin/cms/pages" element={<AdminCMSPages />} />
              <Route path="/admin/cms/pages/new" element={<AdminCMSNewPage />} />
              <Route path="/admin/cms/pages/:id" element={<AdminCMSEditPage />} />
              <Route path="/admin/cms/posts" element={<AdminNewsPosts />} />
              <Route path="/admin/cms/posts/new" element={<AdminNewsEditPost />} />
              <Route path="/admin/cms/posts/:id" element={<AdminNewsEditPost />} />
              <Route path="/admin/pitches" element={<AdminPitches />} />
              <Route path="/admin/pitches/new" element={<AdminCreatePitch />} />
              <Route path="/admin/pitches/:id/edit" element={<AdminCreatePitch />} />
              
              {/* Jobs/Casting Calls */}
              <Route path="/admin/jobs" element={<AdminJobs />} />
              <Route path="/admin/jobs/new" element={<AdminCreateJob />} />
              <Route path="/admin/jobs/:id/edit" element={<AdminCreateJob />} />
              <Route path="/admin/jobs/:id" element={<AdminJobDetail />} />
              
              {/* New Casting System */}
              <Route path="/admin/extras-calls" element={<AdminExtrasCalls />} />
              <Route path="/admin/extras-calls/:callId" element={<AdminExtrasCallSubmissions />} />
              <Route path="/admin/extras-calls/:callId/blasts" element={<AdminExtrasBlasts />} />
              <Route path="/admin/blast-approvals" element={<AdminBlastApprovals />} />
              <Route path="/admin/production-users" element={<AdminProductionUsers />} />
              <Route path="/admin/castings" element={<AdminCastings />} />
              <Route path="/admin/castings/new" element={<AdminCastingNew />} />
              <Route path="/admin/castings/:castingId" element={<AdminCastingDetail />} />
              <Route path="/admin/castings/:castingId/edit" element={<AdminCastingEdit />} />
              <Route path="/admin/pending-approvals" element={<AdminPendingApprovals />} />
              
              {/* Casting Directors Management */}
              <Route path="/admin/casting-directors" element={<AdminCastingDirectors />} />
              
              {/* Talent Packager - Unified workflow for agents */}
              <Route path="/admin/talent-packager" element={<AdminTalentPackager />} />
              
              {/* Featured Talent Management */}
              <Route path="/admin/featured-talent" element={<AdminFeaturedTalent />} />
              
              {/* Testimonials Management */}
              <Route path="/admin/testimonials" element={<AdminTestimonials />} />
              
              {/* Admin Roster Table View */}
              <Route path="/admin/roster" element={<AdminRoster />} />
              <Route path="/admin/background-roster" element={<AdminBackgroundRoster />} />
              <Route path="/admin/background-inquiries" element={<AdminBackgroundInquiries />} />
              <Route path="/admin/background-signups" element={<AdminBackgroundSignups />} />
              <Route path="/admin/background-pool" element={<AdminBackgroundPool />} />
              <Route path="/admin/wardrobe/:projectSlug" element={<AdminWardrobeBreakdown />} />
              <Route path="/admin/headshot-import" element={<AdminHeadshotImport />} />
              <Route path="/admin/roster/:userId/edit" element={<AdminTalentProfileEdit />} />
              
              {/* Site Editor - Visual CMS */}
              <Route path="/admin/site-editor" element={<AdminSiteEditor />} />
              <Route path="/admin/site-editor/settings" element={<AdminSiteSettings />} />
              <Route path="/admin/site-editor/:pageKey" element={<AdminSiteEditor />} />
              
              {/* Talent Jobs Page */}
              <Route path="/talent/jobs" element={<TalentJobs />} />

              {/* Submission Hub — cross-platform casting pipeline */}
              <Route path="/talent/submissions" element={<TalentMySubmissions />} />
              <Route path="/admin/submission-hub" element={<AdminSubmissionHub />} />
              
              {/* Talent Casting Inbox */}
              <Route path="/talent/inbox" element={<TalentInbox />} />
              <Route path="/talent/inbox/:invitationId" element={<TalentAuditionSubmit />} />
              
              {/* Client Management */}
              <Route path="/admin/clients" element={<AdminClients />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/compose" element={<AdminCompose />} />
              <Route path="/admin/campaigns/profile-access" element={<AdminProfileAccessCampaign />} />
              <Route path="/admin/announcements" element={<AdminAnnouncements />} />
              <Route path="/admin/announcements/:announcementId" element={<AdminAnnouncements />} />
              <Route path="/client" element={<ClientDashboard />} />
              
              {/* Talent Portal */}
              <Route path="/talent" element={<TalentDashboard />} />
              <Route path="/talent/edit" element={<TalentEditProfile />} />
              <Route path="/talent/analytics" element={<TalentAnalytics />} />
              <Route path="/talent/media" element={<Navigate to="/talent/profile/edit?section=photos" replace />} />
              <Route path="/talent/resume" element={<TalentResume />} />
              <Route path="/talent/self-tapes" element={<TalentSelfTapes />} />
              <Route path="/talent/tape-requests/:submissionId" element={<TalentTapeRequest />} />
              <Route path="/talent/voice-over" element={<TalentVoiceOver />} />
              <Route path="/talent/settings" element={<TalentSettings />} />
              <Route path="/talent/casting-directors" element={<TalentCastingDirectors />} />
              <Route path="/talent/extras" element={<TalentExtrasCalls />} />

              {/* Production portal (PRODUCTION role) */}
              <Route path="/production" element={<ProductionPortal />} />
              <Route path="/production/projects/:callId" element={<ProductionSubmissions />} />
              <Route path="/production/projects/:callId/blasts" element={<ProductionBlasts />} />
              <Route path="/talent/calendar" element={<TalentCalendar />} />
              <Route path="/talent/reel-split" element={<TalentReelSplit />} />
              <Route path="/talent/profile/edit" element={<TalentProfileEditor />} />
              <Route path="/talent/account" element={<TalentAccount />} />

              {/* Talent Help Center */}
              <Route path="/talent/help" element={<TalentHelp />} />
              <Route path="/talent/help/media" element={<TalentHelpArticle articleId="media" />} />
              <Route path="/talent/help/resume" element={<TalentHelpArticle articleId="resume" />} />
              <Route path="/talent/help/voice-over" element={<TalentHelpArticle articleId="voiceover" />} />
              <Route path="/talent/help/self-tapes" element={<TalentHelpArticle articleId="selftapes" />} />
              <Route path="/talent/help/profile" element={<TalentHelpArticle articleId="profile" />} />
              <Route path="/talent/help/upgrade" element={<TalentHelpArticle articleId="upgrade" />} />
              
              {/* Pitch View (public, may require auth) */}
              <Route path="/pitch/:id" element={<PitchView />} />
              
              {/* Client Pages */}
              <Route path="/saved-talents" element={<SavedTalents />} />
              
              {/* Payment Success Page */}
              <Route path="/payment/success" element={<PaymentSuccess />} />
              
              {/* Public CMS Pages */}
              <Route path="/p/:slug" element={<CMSPage />} />
              
              {/* Public pages with PublicLayout (no wrapper needed - layout is inside component) */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/talents" element={<Talents />} />
              <Route path="/new-faces" element={<NewFaces />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<NewsArticle />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/castings" element={<PublicCastings />} />
              <Route path="/audition/:projectSlug" element={<PublicAuditionProject />} />
              <Route path="/audition/:projectSlug/:roleSlug" element={<PublicAuditionRole />} />
              <Route path="/extras" element={<PublicExtrasCalls />} />
              <Route path="/extras/:callId" element={<PublicExtrasCall />} />
              <Route path="/tape-request/:token" element={<TapeRequestPublic />} />
              <Route path="/tape/:token" element={<TapeShareView />} />
              <Route path="/review/:token" element={<CastingReview />} />
              <Route path="/project-review/:token" element={<ProjectReview />} />
              <Route path="/bg-availability/:token" element={<BackgroundAvailability />} />
              <Route path="/extras-confirm/:token" element={<ExtrasConfirm />} />
              <Route path="/bg-confirm/:token" element={<BgConfirm />} />
              <Route path="/production/extras/:token" element={<PublicProductionShare />} />
              <Route path="/get-profile/:token" element={<GetProfile />} />
              <Route path="/book" element={<Contact />} />
              <Route path="/apply" element={<TalentSubmission />} />
              
              {/* Pages that still need Layout wrapper */}
              <Route
                path="/talents/:id"
                element={
                  <Layout>
                    <TalentProfile />
                  </Layout>
                }
              />
              {/* Catch-all 404 — must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
